import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sports-betting/schedule`;
const LEAGUES = [['nba', 'NBA'], ['nfl', 'NFL'], ['cfb', 'College FB']];
const todayPT = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
const ym = (d) => d.slice(0, 7).replace('-', '');
const shift = (m, n) => { const d = new Date(Number(m.slice(0, 4)), Number(m.slice(4)) - 1 + n, 1); return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}`; };
const monthName = (m) => new Date(Number(m.slice(0, 4)), Number(m.slice(4)) - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
const dayName = (d) => new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

const Team = ({ t }) => (
  <span className="flex min-w-0 items-center gap-1.5">
    {t.logo ? <img src={t.logo} alt="" className="h-6 w-6 shrink-0 object-contain" /> : <span className="h-6 w-6 shrink-0 rounded-full bg-zinc-800" />}
    <span className="truncate text-sm font-semibold text-white">{t.name}</span>
  </span>
);

const GameRow = ({ g }) => (
  <li className="flex items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-900/70 px-3 py-2" data-testid={`sched-game-${g.id}`}>
    <div className="w-20 shrink-0 text-right">
      <p className="font-mono text-sm font-black text-amber-300">{g.state === 'pre' ? g.time.replace(' PT', '') : g.state === 'in' ? 'LIVE' : 'Final'}</p>
      <p className="text-[10px] text-zinc-500">{g.state === 'pre' ? (g.time === 'TBD' ? 'time not set' : 'PT') : g.status}</p>
    </div>
    <div className="min-w-0 flex-1 space-y-0.5">
      <div className="flex items-center justify-between gap-2"><Team t={g.away} />{g.state !== 'pre' && <span className="font-mono text-sm">{g.away.score}</span>}</div>
      <div className="flex items-center justify-between gap-2"><Team t={g.home} />{g.state !== 'pre' && <span className="font-mono text-sm">{g.home.score}</span>}</div>
      <p className="truncate text-[10px] text-zinc-500">{g.tv ? `TV: ${g.tv}` : 'TV: TBA'}{g.note ? ` · ${g.note}` : ''}</p>
    </div>
  </li>
);

const Calendar = ({ month, counts, day, setDay }) => {
  const first = new Date(Number(month.slice(0, 4)), Number(month.slice(4)) - 1, 1);
  const days = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  const cells = [...Array(first.getDay()).fill(null), ...Array.from({ length: days }, (_, i) => `${month.slice(0, 4)}-${month.slice(4)}-${String(i + 1).padStart(2, '0')}`)];
  const today = todayPT();
  return (
    <div className="grid grid-cols-7 gap-1" data-testid="sched-calendar">
      {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => <p key={`h${i}`} className="text-center text-[10px] font-bold text-zinc-500">{d}</p>)}
      {cells.map((d, i) => (d ? (
        <button key={d} type="button" onClick={() => setDay(d)} disabled={!counts[d]} data-testid={`sched-day-${d}`}
          className={`flex aspect-square flex-col items-center justify-center rounded-lg border text-sm transition-colors ${d === day ? 'border-amber-400 bg-amber-500/20 text-white' : counts[d] ? 'border-zinc-700 bg-zinc-900 text-white hover:border-zinc-500' : 'border-transparent text-zinc-600'} ${d === today ? 'ring-1 ring-sky-400' : ''}`}>
          <span className="font-bold leading-none">{Number(d.slice(8))}</span>
          {counts[d] ? <span className="mt-0.5 text-[9px] font-bold leading-none text-amber-300">{counts[d]}</span> : null}
        </button>
      ) : <span key={`e${i}`} />))}
    </div>
  );
};

// Month calendar for NBA / NFL / college football: tap any date to list every game that day.
export default function GameCalendar({ initialLeague = 'nba' }) {
  const [league, setLeague] = useState(initialLeague);
  const [month, setMonth] = useState(ym(todayPT()));
  const [team, setTeam] = useState('');
  const [data, setData] = useState(null);
  const [day, setDay] = useState(todayPT());
  useEffect(() => {
    setData(null);
    axios.get(`${API}/${league}`, { params: { month } }).then((r) => setData(r.data)).catch(() => setData({ games: [], teams: [], error: true }));
  }, [league, month]);
  const games = useMemo(() => (data?.games || []).filter((g) => !team || g.away.abbr === team || g.home.abbr === team), [data, team]);
  const counts = useMemo(() => games.reduce((m, g) => ({ ...m, [g.day]: (m[g.day] || 0) + 1 }), {}), [games]);
  useEffect(() => {  // keep a day with games selected when the month/team changes
    if (!data || counts[day]) return;
    const next = Object.keys(counts).sort().find((d) => d >= todayPT()) || Object.keys(counts).sort()[0];
    if (next) setDay(next);
  }, [counts, data]);
  const list = games.filter((g) => g.day === day);
  return (
    <div className="space-y-4" data-testid="game-calendar">
      <div className="flex flex-wrap items-center gap-2">
        {LEAGUES.map(([k, l]) => (
          <button key={k} type="button" onClick={() => { setLeague(k); setTeam(''); }} data-testid={`sched-league-${k}`}
            className={`rounded-full px-3 py-1 text-sm font-bold ${league === k ? 'bg-amber-400 text-black' : 'border border-zinc-700 text-zinc-300 hover:bg-zinc-800'}`}>{l}</button>
        ))}
        <select value={team} onChange={(e) => setTeam(e.target.value)} className="ml-auto max-w-[45%] rounded-md border border-zinc-700 bg-zinc-900 px-2 py-1 text-sm text-white" data-testid="sched-team-filter">
          <option value="">All teams</option>
          {(data?.teams || []).filter((t) => t.abbr !== 'TBD').map((t) => <option key={t.abbr} value={t.abbr}>{t.name}</option>)}
        </select>
      </div>
      <div className="flex items-center justify-between">
        <button type="button" onClick={() => setMonth(shift(month, -1))} className="rounded-md p-1.5 hover:bg-zinc-800" data-testid="sched-prev-month" aria-label="Previous month"><ChevronLeft className="h-5 w-5" /></button>
        <p className="font-bold" data-testid="sched-month">{monthName(month)} <span className="text-xs font-normal text-zinc-400">· {games.length} games</span></p>
        <button type="button" onClick={() => setMonth(shift(month, 1))} className="rounded-md p-1.5 hover:bg-zinc-800" data-testid="sched-next-month" aria-label="Next month"><ChevronRight className="h-5 w-5" /></button>
      </div>
      {!data ? <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-zinc-500" /></div> : (
        <>
          <Calendar month={month} counts={counts} day={day} setDay={setDay} />
          <div className="space-y-2">
            <p className="text-sm font-bold text-zinc-200" data-testid="sched-day-label">{ym(day) === month ? `${dayName(day)} · ${list.length} game${list.length === 1 ? '' : 's'}` : 'Tap a day'}</p>
            {ym(day) === month && (list.length ? <ul className="space-y-2">{list.map((g) => <GameRow key={g.id} g={g} />)}</ul>
              : <p className="text-sm text-zinc-500" data-testid="sched-empty">{data.error ? 'Could not load the schedule.' : 'No games this day.'}</p>)}
          </div>
        </>
      )}
    </div>
  );
}
